import { useState, useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'
import { Sparkles, X, Send } from 'lucide-react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../lib/auth'
import Markdown from './Markdown'

// Floating help button + chat drawer. Available to every authenticated
// role on every page. Calls the ai-app-assistant edge function which
// returns Anthropic-generated step-by-step help scoped to the user's
// role and current route.
export default function HelpAssistant() {
  const location = useLocation()
  const { user, profile } = useAuth()
  const [open, setOpen] = useState(false)
  const [messages, setMessages] = useState([])
  const [draft, setDraft] = useState('')
  const [sending, setSending] = useState(false)
  const [conversationId, setConversationId] = useState(null)
  const [scrolling, setScrolling] = useState(false)
  const feedRef = useRef(null)

  useEffect(() => {
    if (feedRef.current) feedRef.current.scrollTop = feedRef.current.scrollHeight
  }, [messages, open])

  useEffect(() => {
    let timer
    function onScroll() {
      setScrolling(true)
      clearTimeout(timer)
      timer = setTimeout(() => setScrolling(false), 250)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => {
      window.removeEventListener('scroll', onScroll)
      clearTimeout(timer)
    }
  }, [])

  // Only render UI for authenticated users. Checked AFTER all hooks to
  // keep hook order stable when the auth state transitions.
  if (!user) return null

  async function handleSend(e) {
    e?.preventDefault?.()
    if (!draft.trim() || sending) return
    const content = draft.trim()
    setDraft('')
    setSending(true)
    // Optimistic append
    setMessages(prev => [...prev, { role: 'user', content, id: 'tmp-' + Date.now() }])

    try {
      const { data, error } = await supabase.functions.invoke('ai-app-assistant', {
        body: {
          action: 'send',
          conversation_id: conversationId,
          content,
          current_route: location.pathname,
        },
      })
      if (error) {
        let detail = error.message
        try {
          const body = await error.context?.json?.()
          if (body?.error) detail = body.error
        } catch {}
        throw new Error(detail)
      }
      if (data?.conversation_id) setConversationId(data.conversation_id)
      setMessages(prev => [...prev, { role: 'assistant', content: data?.reply || '(no reply)', id: 'r-' + Date.now() }])
    } catch (err) {
      setMessages(prev => [...prev, { role: 'assistant', content: `Sorry — ${err.message}`, id: 'e-' + Date.now(), isError: true }])
    }
    setSending(false)
  }

  return (
    <>
      {/* Floating button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed z-50 rounded-full flex items-center justify-center hover:scale-105 active:scale-95"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0) + 80px)',
            right: '20px',
            width: scrolling ? '48px' : '56px',
            height: scrolling ? '48px' : '56px',
            background: 'linear-gradient(135deg, #004b4b 0%, #006565 100%)',
            color: '#ffffff',
            boxShadow: '0 12px 32px rgba(23, 28, 31, 0.06), 0 4px 12px rgba(23, 28, 31, 0.08)',
            transition: 'width 200ms ease, height 200ms ease, transform 150ms ease',
          }}
          aria-label="Open help assistant"
        >
          <Sparkles size={scrolling ? 18 : 22} />
        </button>
      )}

      {/* Drawer */}
      {open && (
        <div className="fixed z-50 flex flex-col bg-surface-container-lowest rounded-lg shadow-float overflow-hidden"
          style={{
            bottom: 'calc(env(safe-area-inset-bottom, 0) + 80px)',
            right: '16px',
            left: '16px',
            maxWidth: '400px',
            marginLeft: 'auto',
            height: '70vh',
            maxHeight: '560px',
          }}
        >
          <div className="flex items-center justify-between px-4 py-3" style={{ background: 'linear-gradient(135deg, #004b4b 0%, #006565 100%)' }}>
            <div className="flex items-center gap-2">
              <Sparkles size={16} style={{ color: '#ffffff' }} />
              <span className="text-sm font-semibold tracking-[0.05em] uppercase" style={{ color: '#ffffff' }}>Placed Assistant</span>
            </div>
            <button
              onClick={() => setOpen(false)}
              className="w-7 h-7 rounded-full flex items-center justify-center"
              style={{ background: 'rgba(255,255,255,0.2)', color: '#ffffff' }}
              aria-label="Close help"
            >
              <X size={14} />
            </button>
          </div>

          <div ref={feedRef} className="flex-1 overflow-y-auto p-3 space-y-3">
            {messages.length === 0 ? (
              <div className="text-center py-6">
                <Sparkles size={28} className="mx-auto mb-2 text-primary" strokeWidth={1.5} />
                <p className="text-sm font-semibold text-on-surface">Hi {profile?.display_name?.split(' ')[0] || 'there'}!</p>
                <p className="text-xs text-on-surface-variant mt-1">Ask me anything about Placed. Try:</p>
                <div className="mt-3 space-y-1">
                  {[
                    'How do I log my hours?',
                    'Where are the finance reports?',
                    'How do I assign a foster?',
                  ].map(q => (
                    <button
                      key={q}
                      onClick={() => setDraft(q)}
                      className="block mx-auto text-xs text-primary font-medium hover:underline"
                    >
                      "{q}"
                    </button>
                  ))}
                </div>
              </div>
            ) : (
              messages.map(m => (
                <div key={m.id} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[85%] rounded-md px-3 py-2 text-sm ${
                    m.role === 'user'
                      ? 'bg-primary text-on-primary'
                      : m.isError
                      ? 'bg-error-container text-error'
                      : 'bg-surface-container-low text-on-surface'
                  }`}>
                    {m.role === 'user'
                      ? <p className="whitespace-pre-wrap">{m.content}</p>
                      : <Markdown>{m.content}</Markdown>}
                  </div>
                </div>
              ))
            )}
            {sending && (
              <div className="flex justify-start">
                <div className="bg-surface-container-low text-on-surface-variant rounded-md px-3 py-2 text-xs italic">
                  Thinking…
                </div>
              </div>
            )}
          </div>

          <form onSubmit={handleSend} className="p-3 flex gap-2" style={{ borderTop: '1px solid rgba(200,195,184,0.2)' }}>
            <input
              type="text"
              value={draft}
              onChange={e => setDraft(e.target.value)}
              placeholder="Ask about any page…"
              className="flex-1 px-3 py-2 rounded-md bg-surface-container-low text-sm ghost-border outline-none text-on-surface placeholder:text-outline focus:border-b-2 focus:border-b-primary"
            />
            <button
              type="submit"
              disabled={!draft.trim() || sending}
              className="w-10 h-10 rounded-md flex items-center justify-center disabled:opacity-50"
              style={{ background: '#004b4b', color: '#ffffff' }}
              aria-label="Send"
            >
              <Send size={14} />
            </button>
          </form>
        </div>
      )}
    </>
  )
}
